import * as React from 'react';

export interface VideoChapter {
  time: number;
  title: string;
}

interface VideoSeekbarProps {
  currentTime: number;
  duration: number;
  buffered: number;
  chapters: VideoChapter[];
  progressBarRef: React.RefObject<HTMLButtonElement | null>;
  onProgressClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onProgressMouseDown: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export const VideoSeekbar = ({
  currentTime,
  duration,
  buffered,
  chapters,
  progressBarRef,
  onProgressClick,
  onProgressMouseDown,
}: VideoSeekbarProps) => {
  const [hoverTitle, setHoverTitle] = React.useState<{
    x: number;
    title: string;
  } | null>(null);

  // Show the title of the chapter under the cursor (last chapter whose start is
  // at or before the hovered time). Assumes chapters are sorted by time.
  const handleSeekHover = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (chapters.length === 0 || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const fraction = Math.min(
      1,
      Math.max(0, (e.clientX - rect.left) / rect.width)
    );
    const time = fraction * duration;
    let current: VideoChapter | null = null;
    for (const chapter of chapters) {
      if (chapter.time <= time) current = chapter;
      else break;
    }
    setHoverTitle(
      current ? { x: e.clientX - rect.left, title: current.title } : null
    );
  };

  return (
    <button
      ref={progressBarRef}
      className="w-full h-1 bg-white/30 rounded-full mb-4 hover:h-2 transition-all border-none p-0 relative group/seekbar"
      onClick={onProgressClick}
      onMouseDown={onProgressMouseDown}
      onMouseMove={handleSeekHover}
      onMouseLeave={() => setHoverTitle(null)}
      aria-label="Seek video"
    >
      {/* Buffered (loaded-ahead) indicator */}
      <div
        className="absolute inset-y-0 left-0 bg-white/50 rounded-full pointer-events-none"
        style={{ width: `${duration ? (buffered / duration) * 100 : 0}%` }}
      />

      {/* Played progress */}
      <div
        className="h-full bg-blue-500 rounded-full pointer-events-none relative"
        style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
      >
        {/* Seekbar handle */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-blue-500 rounded-full shadow-lg transition-transform group-hover/seekbar:scale-150" />
      </div>

      {/* Chapter markers */}
      {duration
        ? chapters.map(chapter =>
            chapter.time > 0 && chapter.time < duration ? (
              <div
                key={`${chapter.time}-${chapter.title}`}
                className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-black/40 pointer-events-none"
                style={{ left: `${(chapter.time / duration) * 100}%` }}
              />
            ) : null
          )
        : null}

      {/* Chapter title tooltip on hover */}
      {hoverTitle ? (
        <div
          className="absolute bottom-full mb-2 -translate-x-1/2 max-w-64 truncate rounded bg-black/90 px-2 py-1 text-[11px] text-white pointer-events-none"
          style={{ left: `${hoverTitle.x}px` }}
        >
          {hoverTitle.title}
        </div>
      ) : null}
    </button>
  );
};
